import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { CalendarRange, ChevronLeft, ChevronRight, Check, X, Send, Wand2, Users, Upload, CalendarPlus } from "lucide-react";
import { DutyParticipants, DutyShifts } from "@/components/DutyAdmin";
import { downloadUrl } from "@/lib/download";

const googleLink = (date, slot) => {
  const stamp = (t) => `${date.replace(/-/g, "")}T${(t || "09:00").replace(":", "")}00`;
  const text = encodeURIComponent(`Serviço KW Lead · ${slot.shift_name}`);
  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${text}` +
    `&dates=${stamp(slot.start)}/${stamp(slot.end)}&ctz=Europe/Lisbon`;
};

const shiftMonth = (m, n) => {
  const [y, mm] = m.split("-").map(Number);
  const d = new Date(y, mm - 1 + n, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

export default function DutyRoster() {
  const [search, setSearch] = useSearchParams();
  const month = search.get("mes") || (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  })();
  const setMonth = (m) => setSearch({ mes: m });
  const [data, setData] = useState(null);
  const [swaps, setSwaps] = useState([]);
  const [busy, setBusy] = useState(false);
  const [panel, setPanel] = useState(null);

  const load = () => {
    api.get(`/duty/roster?month=${month}`).then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)));
    api.get("/duty/swaps").then((r) => setSwaps(r.data)).catch(() => setSwaps([]));
  };
  useEffect(load, [month]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (fn, okMsg) => {
    setBusy(true);
    try { const r = await fn(); toast.success(okMsg(r?.data || {})); load(); }
    catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };

  const toggleMe = async () => {
    const next = !data?.me?.opt_in;
    if (!next && !window.confirm("Sair da escala de serviço? Deixas de ser incluído nos próximos meses.")) return;
    await act(() => api.post("/duty/participation", { opt_in: next }),
      () => (next ? "Estás inscrito na escala" : "Saíste da escala"));
  };

  const addToCalendar = async () => {
    setBusy(true);
    try {
      await downloadUrl(`${process.env.REACT_APP_BACKEND_URL}/api/duty/my-calendar?month=${month}`, `escala-kwlead-${month}.ics`);
      toast.success("Calendário descarregado · abre o ficheiro para juntar os dias e as horas");
    } catch (err) { toast.error(err.message || "Não consegui preparar o calendário"); }
    setBusy(false);
  };

  const sendImport = async (payload) => {
    setBusy(true);
    try {
      const r = await api.post(`/duty/roster/${payload.month || month}/import`, {
        shifts: payload.shifts, days: payload.days, replace: true, opt_in: true,
      });
      const miss = r.data.unmatched || [];
      toast.success(`Escala importada · ${r.data.slots} turnos para ${r.data.people} pessoas`);
      if (miss.length) toast.error(`Não encontrei no Plantel: ${miss.map((m) => m.name).join(", ")}`, { duration: 12000 });
      if (payload.month && payload.month !== month) setMonth(payload.month);
      else load();
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };

  const importFile = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "application/json";
    input.onchange = async () => {
      const f = input.files?.[0];
      if (!f) return;
      try { await sendImport(JSON.parse(await f.text())); }
      catch { toast.error("Ficheiro inválido — tem de ser o JSON da escala"); }
    };
    input.click();
  };

  const importNovembro = async () => {
    if (!window.confirm("Importar a escala de novembro de 2026 da imagem enviada? Substitui o que estiver nesse mês.")) return;
    try {
      const r = await fetch("/escalas/novembro-2026.json", { cache: "no-store" });
      await sendImport(await r.json());
    } catch { toast.error("Não consegui ler a escala de novembro"); }
  };

  const published = data?.status === "published";

  return (
    <div className="space-y-8 kw-fade-up" data-testid="duty-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="kw-overline mb-2">KW Lead · serviço mensal</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tighter">
            Escala de <span className="text-[#E50000]">serviço</span>
          </h1>
          <p className="text-sm text-[#A3A3A3] mt-2 max-w-xl">
            Só entra na escala quem quer. Ativa «Quero fazer escala» e ficas incluído na rotação dos próximos meses.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {data?.my_slots > 0 && (
            <button onClick={addToCalendar} disabled={busy} data-testid="duty-add-calendar"
              className="kw-btn-secondary inline-flex items-center gap-2">
              <CalendarPlus className="w-4 h-4" /> Pôr os meus {data.my_slots} dias no calendário
            </button>
          )}
          {data?.me && (
            <button onClick={toggleMe} disabled={busy} data-testid="duty-opt-in"
              className={data.me.opt_in ? "kw-btn-secondary inline-flex items-center gap-2" : "kw-btn-primary inline-flex items-center gap-2"}>
              <Check className="w-4 h-4" /> {data.me.opt_in ? "Estou na escala · sair" : "Quero fazer escala"}
            </button>
          )}
        </div>
      </div>

      <div className="kw-card p-5 flex flex-wrap items-center gap-3" data-testid="duty-month-bar">
        <button onClick={() => setMonth(shiftMonth(month, -1))} className="kw-btn-secondary !px-3 !py-1" data-testid="duty-prev"><ChevronLeft className="w-4 h-4" /></button>
        <div className="flex-1 min-w-[180px]">
          <div className="font-display text-2xl font-black uppercase">{data?.month_label || month}</div>
          <div className="text-[11px] uppercase tracking-widest mt-1" data-testid="duty-status">
            {published ? <span className="text-[#22C55E]">Publicada</span> : <span className="text-[#FFB020]">Rascunho — ainda não está publicada</span>}
            {data?.participants?.length > 0 && <span className="text-[#737373]"> · {data.participants.length} inscritos</span>}
          </div>
        </div>
        <button onClick={() => setMonth(shiftMonth(month, 1))} className="kw-btn-secondary !px-3 !py-1" data-testid="duty-next"><ChevronRight className="w-4 h-4" /></button>
      </div>

      {data?.can_manage && (
        <div className="kw-card p-5 space-y-3" data-testid="duty-manage">
          <div className="kw-overline">Gestão da escala</div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => window.confirm("Gerar uma sugestão rotativa? Substitui o que estiver nesta escala.") &&
              act(() => api.post(`/duty/roster/${month}/generate`), (d) => `Sugestão criada · ${d.slots} turnos por ${d.people} pessoas`)}
              disabled={busy} className="kw-btn-primary inline-flex items-center gap-2 text-xs" data-testid="duty-generate">
              <Wand2 className="w-4 h-4" /> Sugerir escala rotativa
            </button>
            <button onClick={() => act(() => api.post(`/duty/roster/${month}/publish`), (d) => d.emails ? `Publicada · ${d.emails} emails enviados` : "Escala publicada")}
              disabled={busy} className="kw-btn-secondary inline-flex items-center gap-2 text-xs" data-testid="duty-publish">
              <Check className="w-4 h-4" /> Publicar escala
            </button>
            <button onClick={() => window.confirm("Enviar agora um email a cada inscrito com os seus dias?") &&
              act(() => api.post(`/duty/roster/${month}/notify`), (d) => `${d.emails} emails enviados`)}
              disabled={busy} className="kw-btn-secondary inline-flex items-center gap-2 text-xs" data-testid="duty-notify">
              <Send className="w-4 h-4" /> Enviar avisos agora
            </button>
            <button onClick={importFile} disabled={busy} className="kw-btn-secondary inline-flex items-center gap-2 text-xs" data-testid="duty-import">
              <Upload className="w-4 h-4" /> Importar escala (ficheiro)
            </button>
            <button onClick={() => setPanel(panel === "people" ? null : "people")} className="kw-btn-secondary inline-flex items-center gap-2 text-xs" data-testid="duty-people-toggle">
              <Users className="w-4 h-4" /> Participantes
            </button>
            {data.is_admin && (
              <button onClick={() => setPanel(panel === "shifts" ? null : "shifts")} className="kw-btn-secondary text-xs" data-testid="duty-shifts-toggle">
                Turnos e avisos
              </button>
            )}
          </div>
          {data.is_admin && (
            <button onClick={importNovembro} disabled={busy} className="kw-btn-secondary inline-flex items-center gap-2 text-xs"
              data-testid="duty-import-novembro">
              <Upload className="w-4 h-4" /> Importar escala de novembro 2026 (da imagem)
            </button>
          )}
          <p className="text-[11px] text-[#737373]">
            Os emails só saem se os avisos estiverem ligados (ou carregando em «Enviar avisos agora»).
          </p>
          {panel === "people" && <DutyParticipants onChange={load} />}
          {panel === "shifts" && <DutyShifts data={data} onChange={load} />}
        </div>
      )}

      {swaps.length > 0 && (
        <div className="kw-card p-5" data-testid="duty-swaps">
          <div className="kw-overline mb-3">Pedidos de troca</div>
          {swaps.map((s) => (
            <div key={s.id} className="flex flex-wrap items-center gap-3 py-2 border-b border-[#161616] text-sm">
              <span className="flex-1 min-w-[200px]">
                <b>{s.from_name}</b> → {s.to_name} · {s.day_label} · {s.shift_name}
                {s.note && <span className="text-[#737373]"> · «{s.note}»</span>}
                <span className="text-[10px] uppercase tracking-widest ml-2 text-[#FFB020]">{s.status === "pending" ? "à espera de resposta" : "aceite — falta confirmar"}</span>
              </span>
              {s.for_me && s.status === "pending" && (
                <>
                  <button onClick={() => act(() => api.post(`/duty/swaps/${s.id}/respond`, { accept: true }), () => "Troca aceite")}
                    className="kw-btn-primary !py-1 !px-3 text-[10px]" data-testid={`duty-swap-accept-${s.id}`}>Aceito</button>
                  <button onClick={() => act(() => api.post(`/duty/swaps/${s.id}/respond`, { accept: false }), () => "Pedido recusado")}
                    className="kw-btn-secondary !py-1 !px-3 text-[10px]" data-testid={`duty-swap-decline-${s.id}`}>Não posso</button>
                </>
              )}
              {data?.can_manage && s.status === "accepted" && (
                <button onClick={() => act(() => api.post(`/duty/swaps/${s.id}/confirm`), () => "Troca confirmada")}
                  className="kw-btn-primary !py-1 !px-3 text-[10px]" data-testid={`duty-swap-confirm-${s.id}`}>Confirmar troca</button>
              )}
            </div>
          ))}
        </div>
      )}

      {data?.draft_hidden && (
        <div className="kw-card p-5 text-sm text-[#FFB020]" data-testid="duty-draft-note">
          A escala deste mês ainda está a ser preparada. Assim que for publicada, aparece aqui com os dias de cada um.
        </div>
      )}

      <div className="kw-card divide-y divide-[#161616]" data-testid="duty-grid">
        {(data?.days || []).map((d) => (
          <div key={d.date} className={`p-4 ${d.today ? "bg-[#E50000]/5" : d.weekend ? "bg-[#111]" : ""}`} data-testid={`duty-day-${d.date}`}>
            <div className="text-[11px] uppercase tracking-widest text-[#A3A3A3] mb-2">
              {d.label}
              {d.weekend && <span className="text-[#FFB020]"> · fim de semana</span>}
              {d.today && <span className="text-[#E50000]"> · hoje</span>}
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              {d.slots.map((s) => (
                <Slot key={s.shift_id} day={d} slot={s} data={data} month={month} onChange={load} />
              ))}
            </div>
          </div>
        ))}
        {data && data.days.length === 0 && <div className="p-6 text-sm text-[#737373]">Sem dias úteis neste mês.</div>}
      </div>
    </div>
  );
}

function Slot({ day, slot, data, month, onChange }) {
  const [busy, setBusy] = useState(false);
  const canManage = data?.can_manage;

  const assign = async (playerId) => {
    setBusy(true);
    try {
      await api.put(`/duty/roster/${month}/slot`, { date: day.date, shift_id: slot.shift_id, player_id: playerId || null });
      onChange();
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };

  const [swapOpen, setSwapOpen] = useState(false);
  const [to, setTo] = useState("");
  const [note, setNote] = useState("");
  const options = (data?.participants || []).filter((p) => p.id !== slot.player_id);

  const askSwap = async () => {
    if (!to) return toast.error("Escolhe o colega");
    setBusy(true);
    try {
      await api.post("/duty/swaps", { assignment_id: slot.assignment_id, to_player_id: to, note });
      toast.success("Pedido de troca enviado");
      setSwapOpen(false); setTo(""); setNote("");
      onChange();
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };

  return (
    <div className={`border px-3 py-2 ${slot.mine ? "border-[#E50000] bg-[#E50000]/10" : "border-[#1F1F1F]"}`}
      data-testid={`duty-slot-${day.date}-${slot.shift_id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] uppercase tracking-widest text-[#737373]">{slot.shift_name} · {slot.start}–{slot.end}</div>
        {slot.mine && <span className="text-[10px] uppercase tracking-widest text-[#E50000]">és tu</span>}
      </div>
      {canManage ? (
        <select value={slot.player_id || ""} onChange={(e) => assign(e.target.value)} disabled={busy}
          className="mt-1 w-full bg-[#0B0B0B] border border-[#1F1F1F] text-sm px-2 py-1"
          data-testid={`duty-select-${day.date}-${slot.shift_id}`}>
          <option value="">— sem ninguém —</option>
          {(data.participants || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      ) : (
        <div className="mt-1 text-sm font-bold">{slot.player_name || <span className="text-[#555] font-normal">por definir</span>}</div>
      )}
      {slot.swap && (
        <div className="text-[10px] uppercase tracking-widest text-[#FFB020] mt-1">troca pedida a {slot.swap.to_name}</div>
      )}
      {slot.mine && (
        <a href={googleLink(day.date, slot)} target="_blank" rel="noreferrer"
          className="kw-btn-secondary !py-0.5 !px-2 text-[10px] mt-2 mr-2 inline-flex items-center gap-1"
          data-testid={`duty-google-${day.date}-${slot.shift_id}`}>Google Calendar</a>
      )}
      {slot.mine && data?.swaps_enabled && !slot.swap && !swapOpen && (
        <button onClick={() => setSwapOpen(true)} className="kw-btn-secondary !py-0.5 !px-2 text-[10px] mt-2"
          data-testid={`duty-ask-swap-${slot.assignment_id}`}>Pedir troca</button>
      )}
      {swapOpen && (
        <div className="mt-2 space-y-2" data-testid={`duty-swap-form-${slot.assignment_id}`}>
          <select value={to} onChange={(e) => setTo(e.target.value)} className="w-full bg-[#0B0B0B] border border-[#1F1F1F] text-xs px-2 py-1"
            data-testid={`duty-swap-target-${slot.assignment_id}`}>
            <option value="">— a quem pedes a troca? —</option>
            {options.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Nota (opcional)"
            className="w-full bg-[#0B0B0B] border border-[#1F1F1F] text-xs px-2 py-1" data-testid={`duty-swap-note-${slot.assignment_id}`} />
          <div className="flex gap-2">
            <button onClick={askSwap} disabled={busy} className="kw-btn-primary !py-0.5 !px-2 text-[10px]"
              data-testid={`duty-swap-send-${slot.assignment_id}`}>Enviar pedido</button>
            <button onClick={() => setSwapOpen(false)} className="kw-btn-secondary !py-0.5 !px-2 text-[10px]">Cancelar</button>
          </div>
        </div>
      )}
    </div>
  );
}
